/**
 * Wrapper for Stripe payment information to make it behave like a standard
 * form field
 */
import React from 'react';
import PropTypes from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import { withStyles, withTheme } from '@material-ui/styles';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import InputLabel from '@material-ui/core/InputLabel';
import FormHelperText from '@material-ui/core/FormHelperText';
import { CardElement } from 'react-stripe-elements';
import { uiState } from 'react-redux-ui-state';
import StripeInput from './StripeInput';

const propTypes = {
  onChange: PropTypes.func,
  onReset: PropTypes.func,
  onReady: PropTypes.func,
  shouldReset: PropTypes.bool.isRequired,
  className: PropTypes.string,
  theme: PropTypes.shape({
    typography: PropTypes.shape({
      fontSize: PropTypes.number.isRequired,
      fontFamily: PropTypes.string.isRequired,
    }),
    customColors: PropTypes.shape({
      white: PropTypes.string.isRequired,
    }),
  }).isRequired,
  classes: PropTypes.shape({}).isRequired,
  empty: PropTypes.bool.isRequired,
  focused: PropTypes.bool.isRequired,
  complete: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  error: PropTypes.shape({
    code: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }),
  stripeElm: PropTypes.shape({
    clear: PropTypes.func.isRequired,
    addEventListener: PropTypes.func.isRequired,
    removeEventListener: PropTypes.func.isRequired,
  }),
  setUiState: PropTypes.func.isRequired,
};

const defaultProps = {
  className: null,
  onChange: null,
  onReset: null,
  onReady: null,
  error: null,
  stripeElm: null,
};

export class UnstyledStripeInputWrapper extends React.Component {
  constructor(props) {
    super(props);
    this.resetAfterChangeHandler = this.resetAfterChangeHandler.bind(this);
    this.blurHandler = this.blurHandler.bind(this);
    this.focusHandler = this.focusHandler.bind(this);
    this.readyHandler = this.readyHandler.bind(this);
    this.fieldChangeHandler = this.fieldChangeHandler.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { stripeElm, shouldReset } = this.props;
    const stripeElmChanged = prevProps.stripeElm !== stripeElm;
    const shouldResetChanged = prevProps.shouldReset !== shouldReset;
    if ((stripeElmChanged || shouldResetChanged) && stripeElm && shouldReset) {
      // The form needs to be reset AFTER the Stripe input finishes clearing
      stripeElm.addEventListener('change', this.resetAfterChangeHandler);
      stripeElm.clear();
    }
  }

  componentWillUnmount() {
    const { stripeElm } = this.props;
    if (stripeElm) {
      stripeElm.removeEventListener('change', this.resetAfterChangeHandler);
    }
  }

  resetAfterChangeHandler() {
    const { setUiState, onReset, stripeElm } = this.props;
    setUiState({ pristine: true });
    if (onReset) {
      onReset();
    }
    stripeElm.removeEventListener('change', this.resetAfterChangeHandler);
  }

  blurHandler() {
    const { setUiState } = this.props;
    setUiState({ focused: false });
  }

  focusHandler() {
    const { setUiState } = this.props;
    setUiState({ focused: true });
  }

  readyHandler(elm) {
    const { setUiState, onReady } = this.props;
    setUiState({ stripeElm: elm });
    if (onReady) {
      onReady();
    }
  }

  fieldChangeHandler(obj) {
    const { setUiState, onChange } = this.props;
    setUiState({
      error: obj.error,
      empty: obj.empty,
      complete: obj.complete,
      pristine: false,
    });

    if (onChange) {
      onChange(obj);
    }
  }

  render() {
    const {
      className,
      theme,
      classes,
      empty,
      focused,
      error,
      complete,
      pristine,
      stripeElm,
    } = this.props;

    const style = {
      base: {
        fontSize: `${theme.typography.fontSize}px`,
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
        fontWeight: 100,
        '::placeholder': {
          textTransform: 'capitalize',
          color: 'inherit',
        },
      },
    };

    const showPlaceholderLabel = empty && !focused;
    const needsIncompleteError = !focused && !pristine && !error && !complete;
    let errMsg = error ? error.message : null;
    errMsg = errMsg || (needsIncompleteError ? 'Remaining fields required' : null);

    if (showPlaceholderLabel) {
      Object.assign(style.base['::placeholder'], {
        color: 'transparent',
      });
    }
    /**
     * When thinking about refactoring this form wrapper in the future,
     * or adding additional stripe fields that do not come with react-stripe-elements
     * and making them work with Material-UI,
     * refer to this link: https://github.com/mui-org/material-ui/issues/16037
     * and this codesandbox: https://codesandbox.io/s/stripe-0xez4
     * This link may be helpful also: https://github.com/stripe/react-stripe-elements/issues/164
     */
    return (
      <React.Fragment>
        <FormControl fullWidth error={!!errMsg}>
          <InputLabel
            variant="outlined"
            className={showPlaceholderLabel ? classes.indentedLabel : null}
            classes={{ shrink: classes.shrink }}
            shrink={!showPlaceholderLabel}
          >
            Card Number
          </InputLabel>
          <OutlinedInput
            notched // this is required for style overrides
            placeholder="Card Number"
            classes={{ notchedOutline: classes.outline }}
            disabled={!stripeElm}
            fullWidth
            inputComponent={StripeInput}
            /**
             * This is a bit of a hack. Putting the event handlers in the inputProps gets the form
             * to function properly. Keeping them also at this level makes sure the tests don't
             * break. This is likely due to the nature of the shallow rendering in the tests and
             * the way Material UI now does things. Taking the time to rewrite the tests with
             * @testing-library/react may be a cleaner solution.
             */
            onChange={this.fieldChangeHandler}
            onBlur={this.blurHandler}
            onFocus={this.focusHandler}
            inputProps={{
              className,
              style,
              onReady: this.readyHandler,
              onChange: this.fieldChangeHandler,
              onBlur: this.blurHandler,
              onFocus: this.focusHandler,
              component: CardElement,
            }}
          />
        </FormControl>
        {errMsg && <FormHelperText error>{errMsg}</FormHelperText>}
      </React.Fragment>
    );
  }
}

UnstyledStripeInputWrapper.propTypes = propTypes;
UnstyledStripeInputWrapper.defaultProps = defaultProps;

export const uiConfig = {
  name: 'stripeInput',
  state: () => ({
    error: null,
    focused: false,
    empty: true,
    complete: false,
    pristine: true,
    stripeElm: null,
  }),
};

const styles = (theme) => ({
  shrink: {
    padding: '0 8px',
    background: 'white',
  },
  indentedLabel: {
    marginLeft: 30,
  },
  outline: {
    border: `2px solid ${theme.customColors.orange} !important`,
  },
});

export default uiState(uiConfig)(withTheme(withStyles(styles)(UnstyledStripeInputWrapper)));
